import Phaser from 'phaser';
import type { GameEngineConfig, GameEngineEvents, LoadedGame } from './types.js';
import { DEFAULT_ENGINE_CONFIG } from './types.js';

/**
 * GameEngine wraps Phaser and provides a controlled runtime for
 * AI-generated games. All games run inside this sandboxed engine.
 *
 * M0: Basic lifecycle management. Future versions will add
 * resource isolation, performance monitoring, and automated
 * gameplay testing hooks.
 */
export class GameEngine {
  private phaserGame: Phaser.Game | null = null;
  private config: GameEngineConfig;
  private events: GameEngineEvents;
  private currentGame: LoadedGame | null = null;

  constructor(config: Partial<GameEngineConfig> = {}, events: GameEngineEvents = {}) {
    this.config = { ...DEFAULT_ENGINE_CONFIG, ...config };
    this.events = events;
  }

  /**
   * Initialize the Phaser game instance.
   */
  init(scenes: Phaser.Scene[] = []): Phaser.Game {
    if (this.phaserGame) {
      this.destroy();
    }

    const scaleConfig: Phaser.Types.Core.ScaleConfig = {
      mode: this.getScaleMode(),
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: this.config.width,
      height: this.config.height,
    };

    this.phaserGame = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: this.config.width,
      height: this.config.height,
      backgroundColor: this.config.backgroundColor,
      pixelArt: this.config.pixelArt,
      scale: scaleConfig,
      scene: scenes,
    });

    return this.phaserGame;
  }

  /**
   * Load and start a game.
   */
  async loadGame(loadedGame: LoadedGame): Promise<void> {
    this.currentGame = loadedGame;
    this.events.onGameStart?.(loadedGame.game.id);
  }

  /**
   * Get the current Phaser game instance.
   */
  getPhaserGame(): Phaser.Game | null {
    return this.phaserGame;
  }

  /**
   * Get the currently loaded game metadata.
   */
  getCurrentGame(): LoadedGame | null {
    return this.currentGame;
  }

  /**
   * Get the engine configuration.
   */
  getConfig(): GameEngineConfig {
    return { ...this.config };
  }

  /**
   * Pause the current game.
   */
  pause(): void {
    if (this.phaserGame) {
      this.phaserGame.scene.getScenes(true).forEach((scene) => {
        scene.scene.pause();
      });
    }
  }

  /**
   * Resume the current game.
   */
  resume(): void {
    if (this.phaserGame) {
      this.phaserGame.scene.getScenes(true).forEach((scene) => {
        scene.scene.resume();
      });
    }
  }

  /**
   * Destroy the game instance and clean up resources.
   */
  destroy(): void {
    if (this.phaserGame) {
      this.phaserGame.destroy(true);
      this.phaserGame = null;
    }
    this.currentGame = null;
  }

  private getScaleMode(): number {
    switch (this.config.scaleMode) {
      case 'FIT':
        return Phaser.Scale.FIT;
      case 'RESIZE':
        return Phaser.Scale.RESIZE;
      case 'NONE':
      default:
        return Phaser.Scale.NONE;
    }
  }
}
